import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Wrench, Droplets, HardHat, Activity, GitCommit, Layers, Thermometer, ArrowRight } from 'lucide-react';

const services = [
  {
    title: 'Plumbing Repairs & Installations',
    description: 'Leaky pipes, faucet & toilet repairs, new installations. Our licensed plumbers handle all your plumbing needs with fast, professional service.',
    icon: Wrench,
    image: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/sgdjekh6_file_0000000021b871fdbb75717dd70f8b75%20%281%29.png',
    link: '/services/plumbing-repairs',
  },
  {
    title: 'Water Filtration Services',
    description: 'Install & maintain water filtration systems for clean, safe drinking water. Professional installation and ongoing maintenance included.',
    icon: Droplets,
    image: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/6kwztpbl_file_00000000cc5471fda3a539ab5c1f652c.png',
    link: '/services/water-filtration',
  },
  {
    title: 'New Construction Plumbing',
    description: 'Full plumbing for new builds, inspections, compliance. Complete plumbing solutions for new home construction projects.',
    icon: HardHat,
    image: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/tmjl6pvt_FB_IMG_1771192485955.jpg',
    link: '/services/new-construction',
  },
  {
    title: 'Pump Installation & Servicing',
    description: 'Well pumps, sump pumps, pressure systems. Expert installation and maintenance for all types of pump systems.',
    icon: Activity,
    image: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/s9lq11vn_FB_IMG_1771192466965.jpg',
    link: '/services/pump-installation',
  },
  {
    title: 'Blocked Sewers',
    description: 'Clear clogs, maintain sewer lines. Professional sewer snaking for unblocking up to 150ft.',
    icon: GitCommit,
    image: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/sgdjekh6_file_0000000021b871fdbb75717dd70f8b75%20%281%29.png',
    link: '/services/blocked-sewers',
  },
  {
    title: 'In-Floor Heating Systems',
    description: 'In-floor heating solutions for maximum comfort. Modern radiant floor heating installation and service.',
    icon: Layers,
    image: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/tmjl6pvt_FB_IMG_1771192485955.jpg',
    link: '/services/in-floor-heating',
  },
  {
    title: 'Electric Boiler Installation & Servicing',
    description: 'Reliable home heating solutions. Expert electric boiler installation, repair, and maintenance services.',
    icon: Thermometer,
    image: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/s9lq11vn_FB_IMG_1771192466965.jpg',
    link: '/services/electric-boiler',
  },
];

export const Services = () => {
  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-32 bg-[#005B96]" data-testid="services-hero">
        <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/s9lq11vn_FB_IMG_1771192466965.jpg')] bg-cover bg-center opacity-10" />
        <div className="relative container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-semibold mb-6">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
              Comprehensive Plumbing & Heating Services
            </h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">
              Licensed plumbers in Yarmouth NS providing plumbing, in-floor heating, sewer rooting, pump installation, and water filtration services.
            </p>
            <a
              href="tel:902-746-0554"
              className="inline-flex items-center gap-2 bg-white text-[#005B96] px-8 py-4 rounded-full font-bold hover:bg-[#FFD700] hover:text-black transition-all shadow-xl"
              data-testid="services-hero-cta"
            >
              <Phone size={20} />
              <span>Schedule Your Service</span>
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="services-grid">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Link
                key={service.title}
                to={service.link}
                className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                data-testid={`service-${service.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  
                  {/* Icon Badge */}
                  <div className="absolute bottom-4 left-4 bg-[#005B96] p-3 rounded-xl shadow-lg">
                    <service.icon size={24} className="text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="font-bold text-xl text-gray-900 mb-3 group-hover:text-[#005B96] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {service.description}
                  </p>
                  <div className="flex items-center gap-2 text-[#005B96] font-semibold">
                    <span>Learn More</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#005B96]" data-testid="services-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Contact us today for a free consultation. Our licensed plumbers are ready to help with all your plumbing and water filtration needs.
          </p>
          <a
            href="tel:902-746-0554"
            className="inline-flex items-center gap-2 bg-white text-[#005B96] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFD700] hover:text-black transition-all shadow-xl pulse-glow"
            data-testid="services-bottom-cta"
          >
            <Phone size={24} />
            <span>Call 902-746-0554</span>
          </a>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="sticky-cta md:hidden">
        <a
          href="tel:902-746-0554"
          className="flex items-center justify-center gap-2 bg-[#005B96] text-white w-full py-4 rounded-full font-bold"
        >
          <Phone size={20} />
          <span>Call Now – 902-746-0554</span>
        </a>
      </div>
    </div>
  );
};

export default Services;
